import { Component, OnInit, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { PrecioBinanceBloqueComponent } from './precio-binance-bloque.component';
import {
  MainRatesService,
  ValoresFrontendResponse,
  ValoresRegistradosResponse,
  UsdtVesConfigResponse,
  VesUsdtConfigResponse,
  EditorTramosUsdtResponse,
  EstadoFranjasResponse,
  HistorialTramoUsdt,
  HistorialTramosUsdtPagina,
  TasaTramoUsdt,
  EditorTramosClpUsdtResponse,
  EditorTramosUsdtClpResponse,
  MargenesVesResponse,
} from '../../services/main-rates.service';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { EditOperatorModalComponent, EditOperatorModalData } from './edit-operator-modal.component';
import { avisoTasasNoGrabadas, etiquetaMotorCorta, normalizarMargenesVes } from './margenes-ves.rules';
import { EditUsdtVesModalComponent } from './edit-usdt-ves-modal.component';
import { EditVesUsdtModalComponent } from './edit-ves-usdt-modal.component';
import { Clipboard } from '@angular/cdk/clipboard';
import { N58Service, N58ToggleError } from '../../services/n58.service';
import { R4Service } from '../../services/r4.service';
import {
  BancoPlazaService,
  BancoPlazaToggleError,
  BancoPlazaTogglesStatus,
} from '../../services/banco-plaza.service';
import Swal from 'sweetalert2';
import { AdminEditPermissionsService } from 'src/app/shared/services/admin-edit-permissions.service';
import {
  AlertaVariacion,
  RateDriftWatcher,
  RielTasa,
  UMBRAL_VARIACION_PCT,
} from './rate-drift-watcher';
import { MarkupWatchService } from '../../services/markup-watch.service';
import { EditMarkupUmbralModalComponent } from './edit-markup-umbral-modal.component';
import { UMBRAL_MARKUP_RATIO, VerificacionMarkup } from './markup-check';
import {
  ESPERA_CRON_CADA_MS,
  ESPERA_CRON_INTENTOS,
  FilaTasaWeb,
  filasTarjetaDesde,
  filasWebDesdeTramos,
  franjasAplicadas,
  TasaEsperada,
  huellaFilasWeb,
  mensajeSinPermiso,
  ResumenPar,
  resumenFranjas,
  TEXTO_SE_APLICARA,
} from './franjas-editor.rules';
import {
  FranjasEditorComponent,
  FranjasEditorData,
  FranjasGuardadas,
  PasoAplicacion,
  RESULTADO_ABRIR_COMISION,
} from './franjas-editor.component';
import * as TU from './tramos-usdt-editor.rules';
import { statusDe } from './franjas-editor.rules';
import { MONTO_SIM_CLP } from './tramos-clp-usdt-editor.rules';
import { MONTO_SIM_USDT } from './tramos-usdt-ves-editor.rules';
import {
  ParClp,
  rangoTramoClp,
  resumenClp,
  SimulacionClp,
  simularClp,
  VistaBloqueClp,
  vistaCompra,
  vistaVenta,
} from './seccion-clp-usdt.rules';

/** Carga de un bloque de la sección «Tasas CLP ↔ USDT». */
export interface CargaBloqueClp {
  /** Lectura en vuelo (pinta el esqueleto y evita pedir dos veces). */
  cargando: boolean;
  /** 404/503: backend viejo o sin el SQL de los tramos CLP. */
  noDisponible: boolean;
  /** Otro error de la última lectura manual. */
  error: string | null;
  /** Última lectura buena. */
  leido: Date | null;
}

@Component({
  selector: 'app-main-rates',
  templateUrl: './main-rates.component.html',
  styleUrls: ['./main-rates.component.sass'],
})
export class MainRatesComponent implements OnInit, OnDestroy {
  /**
   * Secciones abiertas. Desde el 22/10/2026 (pedido del dueño: el home tiene
   * mucho contenido) el panel arranca SIEMPRE con todo plegado, en cualquier
   * pantalla, y no se restaura lo que el operador abrió la vez anterior (la
   * clave vieja de localStorage, `CLAVE_SECCIONES`, se borra al entrar).
   * Plegada, una sección muestra su resumen de una línea y no consulta datos
   * en vivo: el latido de `iniciarActualizacionesAutomaticas` la salta y al
   * abrirla se lee en el acto.
   */
  expandedSections: Set<string> = new Set();
  static readonly CLAVE_SECCIONES = 'aguacate.panelTasas.secciones';
  static readonly SECCIONES: readonly string[] = [
    'operador', 'bancos', 'tasaWeb', 'tasaWebVesClp', 'franjas', 'usdtVes', 'vesUsdt', 'clpUsdt', 'pagosN58',
  ];
  /** Motivo visible junto a los botones de edición deshabilitados. */
  readonly textoSinPermisoTasas = 'Solo pueden editar los encargados de tasas.';
  /**
   * Puede editar franjas y tramos: `GET rates/franjas/permisos` con respaldo
   * en `puedeEditarTasas`. ABRIR el editor (en solo lectura) puede cualquiera.
   */
  puedeEditarFranjas = false;
  /** Pares con un guardado de franjas que todavía no se ve aplicado. */
  aplicandoFranjas = new Set<'CLP-VES' | 'VES-CLP'>();
  /** Última lectura de operador/bancos/tasa web y de VES→CLP (para «Actualizado hace…»). */
  lastValoresRefresh: Date | null = null;
  lastVesClpRefresh: Date | null = null;
  /**
   * Carga del panel (22/10/2026): cada sección se pinta con su propio dato
   * (con un esqueleto mientras llega) y al desplegarla no se vuelve a pedir lo
   * que se leyó hace menos de `FRESCO_MS`. Los `pidiendo*` / `cargando*` son
   * las lecturas en vuelo: pintan el esqueleto y evitan pedir dos veces lo
   * mismo; los `last*` de abajo son el momento de la última lectura buena.
   */
  static readonly FRESCO_MS = 30_000;
  cargandoRegistrados = false;
  cargandoVesClp = false;
  cargandoResumenFranjas = false;
  private pidiendoValores = false;
  private pidiendoTramosClpVes = false;
  private pidiendoUsdtVes = false;
  private pidiendoVesUsdt = false;
  private pidiendoTramosVesUsdt = false;
  private lastRegistradosRefresh: Date | null = null;
  private lastTramosClpVesRefresh: Date | null = null;
  private lastResumenFranjasRefresh: Date | null = null;
  private lastTramosVesUsdtRefresh: Date | null = null;
  /** Último `rates/franjas/estado` crudo: el editor lo reusa si es fresco. */
  private estadoFranjas: { en: number; estado: EstadoFranjasResponse } | null = null;
  /**
   * Motor de márgenes CLP↔VES (`rates/margenes-ves`, 03/11/2026): spot cruzado
   * y motor activo para las píldoras de «Bancos Venezolanos». `null` = no se
   * pudo leer (o back sin el motor): las píldoras no se pintan. Como el resto
   * del panel, solo se lee con «Bancos» desplegada (al abrirla, en su latido
   * y tras un guardado que lo cambia); plegada, queda marcada vieja.
   */
  margenesVes: MargenesVesResponse | null = null;
  private pidiendoMargenesVes = false;
  private lastMargenesVesRefresh: Date | null = null;
  /** Lectura en vuelo de `rates/margenes-ves`: la nueva la reemplaza (respuestas fuera de orden). */
  private subMargenesVes?: Subscription;
  /**
   * «Actualizar Tasa» está consultando el permiso de márgenes antes de abrir el
   * modal: el botón queda deshabilitado y un segundo clic no abre otro modal.
   */
  abriendoParametros = false;
  /** Tope de la consulta del permiso al abrir «Actualizar Parámetros»; después, la lista del front. */
  static readonly TOPE_PERMISO_MS = 3_000;
  /**
   * Se guardó (parámetros, operador, bancos), pero alguna ruta no grabó sus
   * tasas: el `message` del back, en ámbar arriba del panel (no es un éxito).
   */
  avisoTasas: string | null = null;
  /** Editor de franjas abierto, para avisarle cuando la espera al cron termina. */
  private refEditor?: MatDialogRef<FranjasEditorComponent>;
  valoresFrontend: ValoresFrontendResponse | null = null;
  valoresRegistrados: ValoresRegistradosResponse | null = null;
  loading = false;
  isUpdating = false; // Para actualizaciones sin bloquear la pantalla
  error: string | null = null;
  tasas: any[] = [];
  updatedIndexes: Set<number> = new Set(); // Para trackear qué cards de tasas se actualizaron
  updatedBanks: Set<string> = new Set(); // Para trackear qué bancos se actualizaron
  updatedOperators: Set<string> = new Set(); // Para trackear qué operadores se actualizaron (cbpay, manual)
  bancosActivos: Record<string, boolean> = {
    MERCANTIL: true,
    BANESCO: true,
    VENEZUELA: true,
    PROVINCIAL: true,
  }; // Estado de bancos activos/inactivos

  // VES-CLP
  tasasVesClp: any[] = [];
  updatedVesClpIndexes: Set<number> = new Set();
  /** `true` si `tasasVesClp` salió de `tramos` (franjas dinámicas). */
  tramosVesClpDinamicos = false;

  // Franjas dinámicas CLP→VES: si `rates/ultimas-agrupadas` trae `tramos`, la
  // tarjeta «Tasa web del día» pinta todas las franjas. `null` = lo de siempre
  // (los tres tramos de `tasas`, que también usa «Copiar tasa»).
  tramosWebClpVes: FilaTasaWeb[] | null = null;

  /**
   * Próxima revisión de las tarjetas cuando el back todavía no grabó las tasas
   * de las franjas recién guardadas (ver `esperarFranjasAplicadas`).
   */
  private recargaFranjasId?: any;
  /** El componente ya se destruyó: una respuesta en vuelo no agenda otra revisión. */
  private destruido = false;
  /**
   * Generación de la espera al cron: cada guardado arranca una nueva y la
   * anterior (timer o lectura en vuelo) queda descartada.
   */
  private esperaGen = 0;
  /** Lectura en vuelo de la espera al cron. */
  private esperaSub?: Subscription;

  /** Resumen de la tarjeta «Configurar franjas»: `null` si no se pudo leer. */
  resumenFranjas: ResumenPar[] | null = null;
  /** La última lectura del resumen de franjas fue buena (si no: «sin datos»). */
  resumenFranjasOk = false;

  // USDT-VES
  usdtVesConfig: UsdtVesConfigResponse | null = null;
  loadingUsdtVes = false;
  errorUsdtVes: string | null = null;
  usdtVesUpdated = false;
  lastUsdtVesRefresh: Date | null = null;

  // VES-USDT
  vesUsdtConfig: VesUsdtConfigResponse | null = null;
  loadingVesUsdt = false;
  errorVesUsdt: string | null = null;
  vesUsdtUpdated = false;
  lastVesUsdtRefresh: Date | null = null;

  // Tramos VES→USDT. Sale del GET de operadores (`/editor`): el del cliente ya
  // no trae costo ni márgenes. `null` o sin tramos = el bloque muestra la
  // «Tasa Vigente» de siempre.
  tramosVesUsdt: EditorTramosUsdtResponse | null = null;
  // Últimos cambios: undefined = cargando, null = falló una carga manual,
  // arreglo = la lista (ya sin las ediciones que solo movieron el orden).
  ultimosCambiosVesUsdt: HistorialTramoUsdt[] | null | undefined = undefined;
  simVesUsdtMonto: number | null = TU.MONTO_SIM_VES;

  // Tasas CLP ↔ USDT (22/10/2026): sección propia con los tramos de la compra
  // (CLP→USDT) y la venta (USDT→CLP). Sale de los dos GET `/editor`. Plegada
  // no pide nada; al desplegarla pide los dos (si no son de hace < FRESCO_MS).
  vistaClp: Record<ParClp, VistaBloqueClp | null> = { 'CLP-USDT': null, 'USDT-CLP': null };
  cargaClp: Record<ParClp, CargaBloqueClp> = {
    'CLP-USDT': { cargando: false, noDisponible: false, error: null, leido: null },
    'USDT-CLP': { cargando: false, noDisponible: false, error: null, leido: null },
  };
  simClpMonto: Record<ParClp, number | null> = { 'CLP-USDT': MONTO_SIM_CLP, 'USDT-CLP': MONTO_SIM_USDT };
  readonly paresClp: readonly ParClp[] = ['CLP-USDT', 'USDT-CLP'];
  /**
   * El API cobra el par con el modelo de distancia (lo avisa el bloque de
   * Oscar): se ocultan los KPI del margen mínimo y el tope. false = como antes.
   */
  distanciaClp: Record<ParClp, boolean> = { 'CLP-USDT': false, 'USDT-CLP': false };
  /**
   * Sube cada vez que el panel relee un par CLP (apertura, latido de 2,5 min,
   * tras guardar): el bloque de Oscar relee con él y no queda atrasado.
   */
  recargaClp: Record<ParClp, number> = { 'CLP-USDT': 0, 'USDT-CLP': 0 };

  // Pagos Venezuela — BP prioritario + N58 fallback (toggles independientes)
  n58PagosActivo = false;
  loadingN58Pagos = false;
  n58PagosError: string | null = null;

  bpPagosActivo = false;
  loadingBPPagos = false;
  bpPagosError: string | null = null;

  r4PagosActivo = false;
  loadingR4Pagos = false;
  r4PagosError: string | null = null;

  // Candado de edición: en producción solo los operadores de la allowlist
  // pueden escribir. Los toggles de Pagos Venezuela (N58 / BP / R4) quedan
  // fuera del candado. Arranca en el valor por defecto del servicio (cerrado
  // en prod) para que no haya botones vivos mientras Cognito responde.
  puedeEditar = false;

  // Editores de franjas y tramos: en producción, solo `EDITORES_TASAS_PROD`
  // (lista aparte de la de arriba). En qa y desarrollo, todos.
  puedeEditarTasas = false;

  // Aviso de variación de tasas: el watcher compara cada refresco contra el
  // anterior y estas alertas se pintan arriba del panel hasta que el operador
  // las descarta.
  alertasVariacion: AlertaVariacion[] = [];
  readonly umbralVariacionPct = UMBRAL_VARIACION_PCT;
  private driftWatcher = new RateDriftWatcher();

  // Tasa USD/CLP que aplica Aguacate. Es el mismo número que el dashboard de
  // operación rotula «USDT/CLP Venta». Llega del servicio de vigilancia, que
  // es el mismo que alimenta el aviso global; queda en null si todavía no hay
  // lectura, y en ese caso los dos chips nuevos no se pintan.
  tasaAguacate: number | null = null;
  readonly umbralMarkupRatio = UMBRAL_MARKUP_RATIO;

  // Resultado de comparar la tasa Aguacate contra el spot. Null mientras falte
  // cualquiera de los dos: así el chip de verificación no se pinta a medias.
  verificacionMarkup: VerificacionMarkup | null = null;

  // Intervalo de reaparición del aviso, en segundos. Llega del backend y solo
  // se usa para precargar el modal de configuración.
  reaparicionMarkupSeg = 300;

  // Para actualizaciones en tiempo real
  private valoresSubscription?: Subscription;

  // Suscripción propia: `valoresSubscription` está declarada pero nunca se
  // asigna, así que colgarse de ella sería un no-op y la suscripción quedaría
  // viva después de salir de la pantalla.
  private markupSub?: Subscription;
  private intervalId?: any;

  // Las dos cards cripto tienen su propio latido, más corto que el general: son
  // dos GET baratos y son las que muestran lo que se está cobrando ahora.
  private intervalCriptoId?: any;
  private onVolverAlTab?: () => void;

  /** Bloques «Precio Binance» (USDT→VES y VES→USDT): leen sus propios tramos. */
  @ViewChildren(PrecioBinanceBloqueComponent) bloquesPrecio?: QueryList<PrecioBinanceBloqueComponent>;

  constructor(
    private mainRatesService: MainRatesService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private n58Service: N58Service,
    private bancoPlazaService: BancoPlazaService,
    private r4Service: R4Service,
    private permisos: AdminEditPermissionsService,
    private markupWatch: MarkupWatchService,
  ) {
    this.puedeEditar = this.permisos.valorInicial;
    this.puedeEditarTasas = this.permisos.valorInicial;
    this.puedeEditarFranjas = this.permisos.valorInicial;
    this.restaurarSecciones();
  }

  toggleSection(section: string) {
    if (this.expandedSections.has(section)) {
      this.expandedSections.delete(section);
    } else {
      this.expandedSections.add(section);
      // Plegada no se consultaba: al abrirla se lee en el acto.
      this.refrescarSeccion(section);
    }
  }

  /** Teclado: Enter o espacio sobre el título pliega/despliega, como el clic. */
  toggleSectionTecla(section: string, ev: Event): void {
    ev.preventDefault();
    this.toggleSection(section);
  }

  /** Todo plegado al entrar; la clave que recordaba las secciones se limpia. */
  private restaurarSecciones(): void {
    this.expandedSections = new Set();
    try {
      localStorage.removeItem(MainRatesComponent.CLAVE_SECCIONES);
    } catch {
      /* storage bloqueado: nada que limpiar */
    }
  }

  private abierta(...secciones: string[]): boolean {
    return secciones.some((s) => this.expandedSections.has(s));
  }

  /**
   * Lo que lee cada sección al abrirse (las mismas cargas del latido). Lo que
   * se leyó hace menos de `FRESCO_MS`, o ya está en vuelo, no se vuelve a pedir.
   */
  private refrescarSeccion(section: string): void {
    switch (section) {
      case 'operador':
      case 'bancos':
      case 'tasaWeb':
        if (this.vencido(this.lastRegistradosRefresh, this.cargandoRegistrados)) this.cargarValoresRegistrados(true);
        if (this.vencido(this.lastValoresRefresh, this.pidiendoValores)) this.cargarValoresIniciales(true);
        if (section === 'tasaWeb' && this.vencido(this.lastTramosClpVesRefresh, this.pidiendoTramosClpVes)) {
          this.cargarTramosClpVes();
        }
        // Píldoras del spot cruzado y el motor (viven en «Bancos Venezolanos»).
        if (section === 'bancos' && this.vencido(this.lastMargenesVesRefresh, this.pidiendoMargenesVes)) {
          this.cargarMargenesVes();
        }
        break;
      case 'tasaWebVesClp':
        if (this.vencido(this.lastVesClpRefresh, this.cargandoVesClp)) this.cargarTasasVesClp(true);
        break;
      case 'franjas':
        if (this.vencido(this.lastResumenFranjasRefresh, this.cargandoResumenFranjas)) this.cargarResumenFranjas();
        break;
      case 'usdtVes':
      case 'vesUsdt':
        if (this.vencido(this.lastUsdtVesRefresh, this.pidiendoUsdtVes)) this.cargarUsdtVesConfig(true);
        if (this.vencido(this.lastVesUsdtRefresh, this.pidiendoVesUsdt)) this.cargarVesUsdtConfig(true, false);
        // Plegada, el latido no lee los tramos VES→USDT: al abrirla, si quedaron viejos.
        if (section === 'vesUsdt' && this.vencido(this.lastTramosVesUsdtRefresh, this.pidiendoTramosVesUsdt)) this.cargarTramosVesUsdt(true);
        break;
      case 'clpUsdt':
        for (const par of this.paresClp) {
          const c = this.cargaClp[par];
          if (this.vencido(c.leido, c.cargando)) this.cargarTramosClp(par, true);
        }
        break;
    }
  }

  /** Hay que volver a leer: no está en vuelo y la última lectura buena es vieja (o no hubo). */
  private vencido(momento: Date | null, enVuelo: boolean): boolean {
    return !enVuelo && (!momento || Date.now() - momento.getTime() >= MainRatesComponent.FRESCO_MS);
  }

  /** «Actualizado hace X min»: al día (verde) si la lectura tiene menos de `maxMin`. */
  frescura(momento: Date | null, maxMin: number): { texto: string; alDia: boolean } | null {
    if (!momento) return null;
    const min = Math.floor((Date.now() - momento.getTime()) / 60000);
    return {
      texto: min < 1 ? 'Actualizado hace instantes' : `Actualizado hace ${min} min`,
      alDia: min < maxMin,
    };
  }

  /** «al día» / «desactualizado» para los resúmenes de una línea. */
  estadoDato(momento: Date | null, maxMin: number): string {
    const f = this.frescura(momento, maxMin);
    return !f ? '' : f.alDia ? 'al día' : 'desactualizado';
  }

  /** Número en formato es-CL («1.234,56»). */
  num(v: number | null | undefined, decimales = 2): string {
    const n = Number(v);
    if (v == null || !Number.isFinite(n)) return '—';
    return n.toLocaleString('es-CL', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
  }

  /**
   * Alarma de desvío EN CONTRA de Aguacate (la tasa que se cobra se alejó de
   * la regla para el lado que nos cuesta), para el resumen de la sección
   * plegada. Misma condición que el aviso rojo de adentro.
   */
  get alarmaDesvioUsdtVes(): boolean {
    const d = this.desvioUsdtVes;
    return !!this.usdtVesConfig?.data?.hayCambios && !!d && !d.aFavor;
  }

  get alarmaDesvioVesUsdt(): boolean {
    const d = this.desvioVesUsdt;
    return !!this.vesUsdtConfig?.data?.hayCambios && !!d && !d.aFavor;
  }

  /** Markup sobre el umbral (lo vigila `MarkupWatchService` aunque la sección esté plegada). */
  get alarmaMarkup(): boolean {
    return this.verificacionMarkup?.sobreUmbral === true;
  }

  /** Bancos activos, para el resumen de la sección plegada. */
  get bancosActivosCount(): number {
    return Object.values(this.bancosActivos).filter(Boolean).length;
  }

  /**
   * Tamaño de los modales del panel: en el celular (≤600px) ocupan la
   * pantalla completa y sus botones quedan fijos abajo (`.tasas-dialogo` en
   * styles.sass); en escritorio, el ancho de siempre.
   */
  private tamanoModal(ancho: string, maxAncho = '95vw'): MatDialogConfig {
    const movil = window.innerWidth <= 600;
    return movil
      ? { width: '100vw', maxWidth: '100vw', height: '100vh', maxHeight: '100vh', panelClass: 'tasas-dialogo' }
      : { width: ancho, maxWidth: maxAncho, panelClass: 'tasas-dialogo' };
  }

  ngOnInit(): void {
    this.resolverPermisoEdicion();
    this.cargarValoresIniciales();
    this.cargarValoresRegistrados();
    // Las píldoras del spot cruzado viven en «Bancos»: plegada no se piden (al abrirla, sí).
    if (this.abierta('bancos')) this.cargarMargenesVes();
    this.escucharMarkup();
    this.cargarConfigBancos();
    this.cargarTasasVesClp();
    this.cargarTramosClpVes();
    this.cargarResumenFranjas();
    this.cargarUsdtVesConfig();
    this.cargarVesUsdtConfig();
    // Tasas CLP ↔ USDT: solo si quedó desplegada (plegada no pide nada).
    if (this.abierta('clpUsdt')) this.paresClp.forEach((par) => this.cargarTramosClp(par));
    this.cargarTogglesVE();
    // Iniciar actualizaciones automáticas cada 5 minutos
    this.iniciarActualizacionesAutomaticas();
  }

  /**
   * Lee el correo del operador logueado (Cognito ya cachea el usuario) y decide
   * si puede escribir. Ante cualquier fallo queda en solo lectura.
   */
  private resolverPermisoEdicion(): void {
    this.permisos.puedeEditar().then((puede) => {
      this.puedeEditar = puede;
    });
    this.permisos.puedeEditarTasas().then((puede) => {
      this.puedeEditarTasas = puede;
    });
    this.resolverPermisoFranjas();
  }

  /**
   * Permiso de EDITAR franjas y tramos: manda el back (`rates/franjas/permisos`);
   * si no responde, la lista del front (`puedeEditarTasas`). Abrir y ver el
   * editor (solo lectura) no depende de esto.
   */
  private async resolverPermisoFranjas(topeMs?: number): Promise<boolean> {
    // Con `topeMs`, si el back no contesta a tiempo se usa la lista del front.
    const pedido = this.mainRatesService.obtenerPermisosFranjas();
    const delServidor = await firstValueFrom(topeMs ? pedido.pipe(timeout(topeMs)) : pedido).catch(() => null);
    const puede = typeof delServidor === 'boolean' ? delServidor : await this.permisos.puedeEditarTasas();
    this.puedeEditarFranjas = puede;
    return puede;
  }

  /**
   * Guarda de escritura. El `[disabled]` del template ya bloquea el click; esto
   * cubre el caso de que alguien lo saque desde devtools.
   */
  private bloqueadoPorPermiso(): boolean {
    return this.permisos.bloqueado(
      this.puedeEditar,
      'Tu usuario no tiene permisos para modificar las tasas.',
    );
  }

  /**
   * Guarda de las comisiones USDT→VES y VES→USDT (modal y «Guardar tasa en
   * vivo»). Usa la lista de editores de tasas, la misma que exige el back en
   * prod para `POST rates/usdt-ves/config` y `POST rates/ves-usdt/config`. El
   * permiso se vuelve a consultar en el momento (el botón deshabilitado no
   * alcanza).
   */
  private async bloqueadoPorPermisoTasas(): Promise<boolean> {
    const puede = await this.permisos.puedeEditarTasas();
    this.puedeEditarTasas = puede;
    return this.permisos.bloqueado(
      puede,
      'Tu usuario no tiene permisos para modificar las tasas.',
    );
  }

  /**
   * Si el back cortó con 403, deja visible su mensaje y devuelve true (el
   * llamador no debe tapar el aviso con su toast de error).
   *
   * Con el `ErrorInterceptor` global el 403 llega aplanado a string: el
   * interceptor ya abrió su propio aviso con el `message` del back ("Acceso
   * denegado", sin timer). En ese caso no se abre nada encima, porque
   * SweetAlert muestra un solo popup y el toast lo reemplazaría.
   */
  private avisarSinPermiso(err: any): boolean {
    const sinPermiso = mensajeSinPermiso(err);
    if (!sinPermiso) return Swal.isVisible() && !Swal.getTimerLeft();
    Swal.fire({
      icon: 'warning',
      title: 'Acción no disponible',
      text: sinPermiso,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#f59e0b',
    });
    return true;
  }

  /**
   * Alimenta el watcher con una lectura y, si el salto supera el umbral, deja
   * la alerta arriba del panel. Solo se guarda la última alerta por riel: si
   * la tasa vuelve a saltar, el aviso se reemplaza con el dato fresco.
   */
  private registrarLectura(riel: RielTasa, valor: number | null | undefined): void {
    const alerta = this.driftWatcher.registrar(riel, valor);
    if (!alerta) return;

    this.alertasVariacion = [
      alerta,
      ...this.alertasVariacion.filter((a) => a.riel !== riel),
    ];
  }

  descartarAlerta(riel: RielTasa): void {
    this.alertasVariacion = this.alertasVariacion.filter((a) => a.riel !== riel);
  }

  descartarTodasLasAlertas(): void {
    this.alertasVariacion = [];
  }

  ngOnDestroy(): void {
    // Limpiar suscripciones
    if (this.valoresSubscription) {
      this.valoresSubscription.unsubscribe();
    }
    this.markupSub?.unsubscribe();
    // Limpiar interval de actualizaciones automáticas
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.intervalCriptoId) {
      clearInterval(this.intervalCriptoId);
    }
    this.destruido = true;
    this.cancelarEsperaFranjas();
    this.subMargenesVes?.unsubscribe();
    if (this.onVolverAlTab) {
      document.removeEventListener('visibilitychange', this.onVolverAlTab);
      window.removeEventListener('focus', this.onVolverAlTab);
    }
  }

  /**
   * Carga los valores iniciales del sistema
   */
  cargarValoresIniciales(isAutoUpdate = false): void {
    // Si es carga inicial, usar loading completo
    // Si es actualización automática, usar isUpdating
    if (!isAutoUpdate) {
      this.loading = true;
    } else {
      this.isUpdating = true;
    }
    this.error = null;
    this.pidiendoValores = true;

    this.mainRatesService.obtenerValoresFrontend().subscribe({
      next: (response) => {
        this.pidiendoValores = false;

        if (response.data) {

          // Convertir tasasActuales a array compatible con main-retail
          // El backend retorna: { rangoA: {monto, fuente, client, admin}, rangoB: {...}, rangoC: {...} }
          // Necesitamos array[6]: [0-2]=Client (verde), [3-5]=Admin (web)
          if (response.data.tasasActuales) {
            const { rangoA, rangoB, rangoC } = response.data.tasasActuales;

            const nuevasTasas = [
              // Tasas Verde (Client) - índices 0, 1, 2
              {
                rango: this.extractRangoNumerico(rangoA.monto),
                descripcion: `1 CLP = ${rangoA.client?.toFixed(4)}VES`,
                tasa: rangoA.client,
                fuente: rangoA.fuente,
                perfil: 'Client',
              },
              {
                rango: this.extractRangoNumerico(rangoB.monto),
                descripcion: `1 CLP = ${rangoB.client?.toFixed(4)}VES`,
                tasa: rangoB.client,
                fuente: rangoB.fuente,
                perfil: 'Client',
              },
              {
                rango: this.extractRangoNumerico(rangoC.monto),
                descripcion: `1 CLP = ${rangoC.client?.toFixed(4)}VES`,
                tasa: rangoC.client,
                fuente: rangoC.fuente,
                perfil: 'Client',
              },
              // Tasas Web (Admin) - índices 3, 4, 5
              {
                rango: this.extractRangoNumerico(rangoA.monto),
                descripcion: `1 CLP = ${rangoA.admin?.toFixed(4)}VES`,
                tasa: rangoA.admin,
                fuente: rangoA.fuente,
                perfil: 'Admin',
              },
              {
                rango: this.extractRangoNumerico(rangoB.monto),
                descripcion: `1 CLP = ${rangoB.admin?.toFixed(4)}VES`,
                tasa: rangoB.admin,
                fuente: rangoB.fuente,
                perfil: 'Admin',
              },
              {
                rango: this.extractRangoNumerico(rangoC.monto),
                descripcion: `1 CLP = ${rangoC.admin?.toFixed(4)}VES`,
                tasa: rangoC.admin,
                fuente: rangoC.fuente,
                perfil: 'Admin',
              },
            ];

            // Detectar qué valores cambiaron
            if (isAutoUpdate && this.tasas.length > 0) {
              this.updatedIndexes.clear();
              nuevasTasas.forEach((nuevaTasa, index) => {
                if (
                  this.tasas[index] &&
                  this.tasas[index].tasa !== nuevaTasa.tasa
                ) {
                  this.updatedIndexes.add(index);
                }
              });

              // Limpiar los indicadores después de 30 segundos
              if (this.updatedIndexes.size > 0) {
                setTimeout(() => {
                  this.updatedIndexes.clear();
                }, 30000);
              }
            }

            this.tasas = nuevasTasas;

          }
        }

        this.valoresFrontend = response;
        this.lastValoresRefresh = new Date();
        this.loading = false;
        this.isUpdating = false;
      },
      error: (error: any) => {
        this.pidiendoValores = false;
        this.error = error.message || 'Error al cargar los valores del sistema';
        this.loading = false;
        this.isUpdating = false;
      },
    });
  }

  /**
   * Carga los valores registrados de Binance y otros operadores
   */
  cargarValoresRegistrados(isAutoUpdate = false): void {
    this.cargandoRegistrados = true;
    this.mainRatesService.obtenerValoresRegistrados().subscribe({
      next: (response) => {
        this.cargandoRegistrados = false;
        this.lastRegistradosRefresh = new Date();

        // Detectar cambios en los valores si es una actualización automática
        if (isAutoUpdate && this.valoresRegistrados?.data && response.data) {
          this.updatedBanks.clear();
          this.updatedOperators.clear();

          // Detectar cambios en bancos
          const bancosActuales = this.valoresRegistrados.data.binance.bancos;
          const bancosNuevos = response.data.binance.bancos;

          if (bancosActuales.banesco !== bancosNuevos.banesco) {
            this.updatedBanks.add('banesco');
          }
          if (bancosActuales.mercantil !== bancosNuevos.mercantil) {
            this.updatedBanks.add('mercantil');
          }
          if (bancosActuales.provincial !== bancosNuevos.provincial) {
            this.updatedBanks.add('provincial');
          }
          if (bancosActuales.venezuela !== bancosNuevos.venezuela) {
            this.updatedBanks.add('venezuela');
          }

          // Detectar cambios en operadores
          if (
            this.valoresRegistrados.data.cbpay.valor !==
            response.data.cbpay.valor
          ) {
            this.updatedOperators.add('cbpay');
          }
          if (
            this.valoresRegistrados.data.manual.valor !==
            response.data.manual.valor
          ) {
            this.updatedOperators.add('manual');
          }

          // Limpiar indicadores después de 30 segundos
          if (this.updatedBanks.size > 0 || this.updatedOperators.size > 0) {
            setTimeout(() => {
              this.updatedBanks.clear();
              this.updatedOperators.clear();
            }, 30000);
          }
        }

        this.valoresRegistrados = response;
        this.registrarLectura('USD_CLP', response.data?.usdClp);
      },
      error: (error: any) => {
        // No mostramos error general porque esto es información adicional
        this.cargandoRegistrados = false;
      },
    });
  }

  /**
   * Spot cruzado CLP↔VES y motor activo (`rates/margenes-ves`) para las
   * píldoras de «Bancos Venezolanos». Es información adicional: si falla, o
   * el back todavía no tiene el motor, las píldoras simplemente no se pintan
   * (no se dejan números viejos a la vista).
   */
  cargarMargenesVes(): void {
    this.pidiendoMargenesVes = true;
    // La lectura anterior en vuelo se cancela: una respuesta vieja no pisa a la nueva.
    this.subMargenesVes?.unsubscribe();
    this.subMargenesVes = this.mainRatesService.obtenerMargenesVes().subscribe({
      next: (r) => {
        this.pidiendoMargenesVes = false;
        this.lastMargenesVesRefresh = new Date();
        this.margenesVes = normalizarMargenesVes(r);
      },
      error: () => {
        this.pidiendoMargenesVes = false;
        this.margenesVes = null;
      },
    });
  }

  /**
   * Algo cambió el spot cruzado o el motor (operador, parámetros, bancos,
   * márgenes, franjas): con «Bancos» desplegada se relee ya; plegada, se
   * marca vieja (sin números viejos guardados) y se lee al abrirla.
   */
  private refrescarMargenesVes(): void {
    if (this.abierta('bancos')) {
      this.cargarMargenesVes();
      return;
    }
    this.subMargenesVes?.unsubscribe();
    this.pidiendoMargenesVes = false;
    this.lastMargenesVesRefresh = null;
    this.margenesVes = null;
  }

  /** El `message` del back como aviso si alguna ruta no grabó sus tasas (ver `avisoTasasNoGrabadas`). */
  private revisarTasasGrabadas(r: any, porSuccess = false): void {
    this.avisoTasas = avisoTasasNoGrabadas(r, porSuccess);
  }

  descartarAvisoTasas(): void {
    this.avisoTasas = null;
  }

  /** «Márgenes» / «Legado» para la píldora del motor. */
  etiquetaMotor(m: string | null | undefined): string {
    return etiquetaMotorCorta(m === 'MARGENES' || m === 'LEGADO' ? m : null);
  }

  /**
   * El markup lo calcula el servicio de vigilancia, que es el mismo que
   * alimenta el aviso global. El chip solo lo muestra.
   *
   * Tener una sola fuente es el punto: si la fila hiciera su propia cuenta,
   * en algún refresco mostraría un porcentaje distinto al del modal y el
   * operador no sabría a cuál creerle.
   */
  private escucharMarkup(): void {
    this.markupWatch.iniciar();
    this.markupSub = this.markupWatch.estado$.subscribe((estado) => {
      this.verificacionMarkup = estado?.verificacion ?? null;
      this.tasaAguacate = estado?.verificacion.aguacate ?? null;
      this.reaparicionMarkupSeg = estado?.reaparicionSeg ?? this.reaparicionMarkupSeg;
    });
  }

  /**
   * Edición del umbral de markup. En producción el backend rechaza a quien no
   * sea el encargado de la tasa; el candado de acá solo evita ofrecer un botón
   * que iba a fallar.
   */
  abrirModalUmbralMarkup(): void {
    if (this.bloqueadoPorPermiso()) return;
    if (!this.verificacionMarkup) return;

    const ref = this.dialog.open(EditMarkupUmbralModalComponent, {
      data: {
        maxRatio: this.verificacionMarkup.umbral,
        reaparicionSeg: this.reaparicionMarkupSeg,
      },
      ...this.tamanoModal('420px', '95vw'),
    });

    ref.afterClosed().subscribe((config) => {
      if (!config) return;

      this.mainRatesService.guardarConfigMarkup(config).subscribe({
        next: () => {
          // Relectura inmediata: el chip tiene que reflejar el umbral nuevo
          // sin esperar el latido.
          this.markupWatch.revisarAhora();
          Swal.fire({
            icon: 'success',
            title: 'Umbral actualizado',
            text: `El markup máximo quedó en ${((config.maxRatio - 1) * 100).toLocaleString('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} %.`,
            confirmButtonColor: '#1C593D',
          });
        },
        error: (error: any) => {
          Swal.fire({
            icon: 'error',
            title: 'No se pudo guardar',
            text: error?.message || 'El backend rechazó el cambio de umbral.',
            confirmButtonColor: '#dc2626',
          });
        },
      });
    });
  }

  /**
   * Inicia actualizaciones automáticas cada 2.5 minutos
   * El backend actualiza cada 3 minutos, por lo que 2.5 minutos garantiza
   * que el frontend siempre capture las actualizaciones sin perder ninguna
   */
  iniciarActualizacionesAutomaticas(): void {
    // Actualizar valores cada 2.5 minutos (150000 ms)
    // Solo lo de las secciones abiertas: plegadas quedan «en pausa».
    this.intervalId = setInterval(() => {
      if (this.abierta('operador', 'bancos', 'tasaWeb')) {
        this.cargarValoresRegistrados(true); // true indica que es actualización automática
        this.cargarValoresIniciales(true); // true indica que es actualización automática
      }
      if (this.abierta('bancos')) this.cargarMargenesVes(); // Spot cruzado y motor CLP↔VES (píldoras de Bancos)
      if (this.abierta('tasaWebVesClp')) this.cargarTasasVesClp(true); // Tasas VES-CLP
      if (this.abierta('tasaWeb')) this.cargarTramosClpVes(); // Franjas dinámicas CLP→VES
      if (this.abierta('clpUsdt')) this.paresClp.forEach((par) => this.cargarTramosClp(par, true)); // Tramos CLP↔USDT
      // Config USDT↔VES: la lee el latido de 60 s de abajo (acá se repetía).
    }, 150000); // 2.5 minutos = 150000 ms

    // USDT↔VES aparte, cada minuto: son dos GET livianos y son los números que
    // el operador compara contra Binance. Con 2.5 minutos podía estar mirando
    // una tasa de hace un rato sin ninguna señal de que era vieja.
    // Aunque estén plegadas: la alarma de desvío del resumen sale de acá.
    // Los tramos VES→USDT (editor + últimos cambios) solo con su sección abierta.
    this.intervalCriptoId = setInterval(() => {
      this.cargarUsdtVesConfig(true);
      this.cargarVesUsdtConfig(true, this.abierta('vesUsdt'));
    }, 60000);

    // Y al volver a la pestaña, refresco inmediato. Es el caso real: el operador
    // deja el panel de fondo, la máquina suspende o el navegador estrangula los
    // timers, y vuelve a una pantalla congelada sin saberlo.
    this.onVolverAlTab = () => {
      if (document.hidden) return;
      const desde = this.lastUsdtVesRefresh?.getTime() ?? 0;
      if (Date.now() - desde < 30000) return; // recién actualizado, no insistir
      this.cargarUsdtVesConfig(true);
      this.cargarVesUsdtConfig(true, this.abierta('vesUsdt'));
    };
    document.addEventListener('visibilitychange', this.onVolverAlTab);
    window.addEventListener('focus', this.onVolverAlTab);
  }

  /**
   * Hace cuánto se leyó la tasa que está en pantalla. El dato ya se guardaba y
   * no se mostraba en ningún lado: sin esto no hay forma de distinguir un número
   * de hace diez segundos de uno de hace diez minutos.
   */
  hace(momento: Date | null): string {
    if (!momento) return '';
    const seg = Math.round((Date.now() - momento.getTime()) / 1000);
    if (seg < 60) return 'hace instantes';
    const min = Math.round(seg / 60);
    return min === 1 ? 'hace 1 minuto' : `hace ${min} minutos`;
  }

  /**
   * El desvío entre lo que se está cobrando y lo que dice la regla configurada.
   *
   * Lo que importa acá no es el tamaño del desvío sino **para qué lado se va la
   * plata**, y eso depende del riel: en USDT→VES el cliente recibe bolívares, así
   * que cobrar por debajo de la regla nos favorece; en VES→USDT el cliente paga
   * bolívares, así que cobrar por debajo nos cuesta. Es el mismo signo con la
   * lectura opuesta, y confundirlo es leer una fuga como si fuera una ganancia.
   *
   * `difPor100` es la diferencia en bolívares sobre una operación de 100 USDT:
   * un porcentaje solo no le dice a nadie cuánta plata es.
   */
  private desvio(
    cobra: number | null | undefined,
    regla: number | null | undefined,
    riel: 'USDT_VES' | 'VES_USDT',
  ): { cobra: number; regla: number; pct: number; aFavor: boolean; difPor100: number } | null {
    const c = Number(cobra);
    const r = Number(regla);
    if (!c || !r || !isFinite(c) || !isFinite(r)) return null;

    return {
      cobra: c,
      regla: r,
      pct: Math.abs((c - r) / r) * 100,
      aFavor: riel === 'USDT_VES' ? c < r : c > r,
      difPor100: Math.abs(c - r) * 100,
    };
  }

  get desvioUsdtVes() {
    return this.desvio(
      this.usdtVesConfig?.data?.tasaVigente?.tasaFinal,
      this.usdtVesConfig?.data?.datosEnVivo?.tasaFinalCalculada,
      'USDT_VES',
    );
  }

  get desvioVesUsdt() {
    return this.desvio(
      this.vesUsdtConfig?.data?.tasaVigente?.tasaFinal,
      this.vesUsdtConfig?.data?.datosEnVivo?.tasaFinalCalculada,
      'VES_USDT',
    );
  }

  /**
   * Calcula las tasas sin guardarlas
   */
  calcularTasas(): void {
    this.loading = true;
    this.error = null;

    this.mainRatesService.calcularTasas().subscribe({
      next: (resultado) => {
        // Recargar valores después de calcular
        this.cargarValoresIniciales();
      },
      error: (error: any) => {
        this.error = error.message || 'Error al calcular tasas';
        this.loading = false;
      },
    });
  }

  /**
   * Calcula y guarda las tasas
   */
  calcularYGuardarTasas(): void {
    this.loading = true;
    this.error = null;
    this.avisoTasas = null;

    this.mainRatesService.calcularYGuardarTasas().subscribe({
      next: (resultado) => {
        // `success: false` = alguna ruta no se grabó: su `message`, como aviso.
        this.revisarTasasGrabadas(resultado, true);
        // Recargar valores después de guardar
        this.cargarValoresIniciales();
        // El markup acaba de cambiar por esta misma acción: no esperamos al
        // latido de 60 s para decírselo al operador que la movió.
        this.markupWatch.revisarAhora();
      },
      error: (error: any) => {
        this.error = error.message || 'Error al calcular y guardar tasas';
        this.loading = false;
      },
    });
  }

  /**
   * Cambia el operador activo
   */
  cambiarOperador(operador: 'MANUAL' | 'BINANCE' | 'CBPAY'): void {
    if (this.bloqueadoPorPermiso()) return;
    this.loading = true;
    this.error = null;
    this.avisoTasas = null;


    this.mainRatesService.cambiarOperador(operador).subscribe({
      next: (resultado) => {
        // `tasasGrabadas` con alguna en false: el `message` del back como aviso.
        this.revisarTasasGrabadas(resultado);
        // Recargar valores después de cambiar operador
        this.cargarValoresIniciales();
        this.cargarUsdtVesConfig(); // Actualizar también la tasa USDT-VES
        this.cargarVesUsdtConfig(); // Actualizar también la tasa VES-USDT
        this.refrescarMargenesVes(); // El spot cruzado usa el VES/USDT del operador activo
      },
      error: (error: any) => {
        this.error = error.message || 'Error al cambiar operador';
        this.loading = false;
      },
    });
  }

  /**
   * Actualiza parámetros de un operador
   * El email del usuario será obtenido por el backend desde Cognito
   */
  actualizarParametros(
    operador: 'MANUAL' | 'BINANCE' | 'CBPAY',
    parametros: {
      descuentoFiat?: number;
      comision?: number;
      comisionVesClp?: number;
      rateVesUsdt?: number;
      usdClp?: number;
    },
  ): void {
    this.loading = true;
    this.error = null;
    this.avisoTasas = null;


    this.mainRatesService
      .actualizarParametros({
        operador,
        ...parametros,
      })
      .subscribe({
        next: (resultado) => {
          // `tasasGrabadas` con alguna en false: el `message` del back como aviso.
          this.revisarTasasGrabadas(resultado);
          // Recargar valores después de actualizar (tanto frontend como registrados)
          this.cargarValoresIniciales();
          this.cargarValoresRegistrados();
          this.cargarUsdtVesConfig(); // Actualizar también la tasa USDT-VES
        this.cargarVesUsdtConfig(); // Actualizar también la tasa VES-USDT
          this.refrescarMargenesVes(); // USD/CLP o VES/USDT nuevos: otro spot cruzado
          this.markupWatch.revisarAhora(); // el markup cambió con este guardado
        },
        error: (error: any) => {
          this.error = error.message || 'Error al actualizar parámetros';
          this.loading = false;
        },
      });
  }

  /**
   * Abre el modal para editar parámetros de operador.
   *
   * Su sección «Márgenes CLP ↔ VES» usa el mismo permiso que el editor de
   * franjas: se consulta en el momento (manda el back, `rates/franjas/permisos`,
   * con respaldo en la lista del front si no contesta en `TOPE_PERMISO_MS`) y
   * sin él la sección abre en solo lectura.
   */
  async abrirModalEditarOperador(): Promise<void> {
    if (this.bloqueadoPorPermiso() || this.abriendoParametros) return;
    if (!this.valoresFrontend?.data) {
      this.error = 'No hay datos disponibles para editar';
      return;
    }

    // Mientras se consulta el permiso el botón queda deshabilitado y un segundo
    // clic no abre otro modal.
    this.abriendoParametros = true;
    let puedeEditarMargenes = false;
    try {
      puedeEditarMargenes = await this.resolverPermisoFranjas(MainRatesComponent.TOPE_PERMISO_MS);
    } finally {
      this.abriendoParametros = false;
    }
    // Si el operador salió de la pantalla mientras tanto, no se abre nada.
    if (this.destruido) return;
    // Releído después del await: el latido pudo traer valores nuevos.
    const datos = this.valoresFrontend?.data;
    if (!datos) return;

    const data: EditOperatorModalData = {
      operadorActivo: datos.operadorActivo,
      puedeEditarMargenes,
      parametros: {
        manual: {
          operador: 'MANUAL',
          descuentoFiat:
            datos.parametrosManual.descuentoFiat,
          comision: datos.parametrosManual.comision,
          comisionVesClp: (datos as any).comisionVesCLP || 0,
          rateVesUsdt: datos.parametrosManual.rateVesUsdt,
          usdClp: datos.parametrosManual.usdClp,
        },
        binance: {
          operador: 'BINANCE',
          descuentoFiat:
            datos.parametrosBinance.descuentoFiat,
          comision: datos.parametrosBinance.comision,
          comisionVesClp: (datos as any).comisionVesCLP || 0,
          rateVesUsdt:
            datos.parametrosBinance.rateVesUsdt,
          usdClp: datos.parametrosBinance.usdClp,
        },
        cbpay: {
          operador: 'CBPAY',
          descuentoFiat:
            datos.parametrosCbpay.descuentoFiat,
          comision: datos.parametrosCbpay.comision,
          comisionVesClp: (datos as any).comisionVesCLP || 0,
          rateVesUsdt: datos.parametrosCbpay.rateVesUsdt,
          usdClp: datos.parametrosCbpay.usdClp,
        },
      },
    };

    // 680px: las tablas del reparto por franja no se aprietan (03/11/2026).
    const dialogRef = this.dialog.open(EditOperatorModalComponent, {
      ...this.tamanoModal('680px', '90vw'),
      data,
    });

    // Los márgenes se guardan dentro del modal (sin cerrarlo): se refresca en el acto.
    const subMargenes = dialogRef.componentInstance?.margenesGuardados?.subscribe(() => this.trasGuardarMargenes());

    dialogRef.afterClosed().subscribe((result) => {
      subMargenes?.unsubscribe();
      if (result) {

        const operadorSeleccionado = result.operador;
        const operadorActual = this.valoresFrontend?.data?.operadorActivo;

        // Verificar si se cambió el operador
        const cambioOperador =
          operadorActual &&
          operadorSeleccionado.toUpperCase() !== operadorActual.toUpperCase();

        if (cambioOperador) {

          this.loading = true;
          this.error = null;
          this.avisoTasas = null;

          // Primero actualizar los parámetros del operador seleccionado
          this.mainRatesService
            .actualizarParametros({
              operador: operadorSeleccionado,
              descuentoFiat: result.descuentoFiat,
              comision: result.comision,
              comisionVesClp: result.comisionVesClp,
              rateVesUsdt: result.rateVesUsdt,
              usdClp: result.usdClp,
            })
            .subscribe({
              next: (resultadoParams) => {
                // Si estas tasas no se grabaron, el aviso queda (el cambio de operador lo actualiza).
                this.revisarTasasGrabadas(resultadoParams);

                // Luego cambiar el operador activo (esto recalcula las tasas)
                this.mainRatesService
                  .cambiarOperador(operadorSeleccionado)
                  .subscribe({
                    next: (resultadoOperador) => {
                      // Lo último que grabó tasas manda: si el cambio de operador las
                      // grabó, el aviso de los parámetros ya no aplica.
                      this.revisarTasasGrabadas(resultadoOperador);
                      // Recargar todos los valores
                      this.cargarValoresIniciales();
                      this.cargarValoresRegistrados();
                      this.cargarUsdtVesConfig(); // Actualizar también la tasa USDT-VES
        this.cargarVesUsdtConfig(); // Actualizar también la tasa VES-USDT
                      this.refrescarMargenesVes(); // Otro operador activo: otro spot cruzado
                    },
                    error: (error: any) => {
                      this.error = error.message || 'Error al cambiar operador';
                      this.loading = false;
                    },
                  });
              },
              error: (error: any) => {
                this.error = error.message || 'Error al actualizar parámetros';
                this.loading = false;
              },
            });
        } else {
          // Solo actualizar parámetros del operador actual
          // El email será capturado por el backend desde Cognito
          this.actualizarParametros(operadorSeleccionado, {
            descuentoFiat: result.descuentoFiat,
            comision: result.comision,
            comisionVesClp: result.comisionVesClp,
            rateVesUsdt: result.rateVesUsdt,
            usdClp: result.usdClp,
          });
        }
      }
    });
  }

  /**
   * Márgenes o motor CLP↔VES guardados desde el modal: con el motor Márgenes
   * cambian las tasas CLP↔VES. Se releen las píldoras del spot cruzado, las
   * tarjetas «Tasa web del día» y los valores del panel.
   */
  private trasGuardarMargenes(): void {
    this.refrescarMargenesVes();
    this.recargarTarjetasFranjas();
    this.cargarValoresIniciales(true);
  }

  /**
   * Extrae el rango numérico del formato de texto
   * Ejemplo: "10,000 - 99,999 CLP" -> "10000-99999"
   */
  private extractRangoNumerico(rangoTexto: string): string {
    const numeros = rangoTexto
      .replace(/,/g, '')
      .match(/\d+/g)
      ?.map((n) => parseInt(n));
    if (numeros && numeros.length >= 2) {
      return `${numeros[0]}-${numeros[1]}`;
    }
    return rangoTexto;
  }

  /**
   * Copia las tasas al portapapeles
   */
  copiarTasa(): void {
    if (!this.tasas || this.tasas.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'No hay tasas disponibles',
        text: 'Por favor, carga las tasas primero.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    const message = this.setMessage();
    this.clipboard.copy(message);

    Swal.fire({
      icon: 'success',
      title: 'Tasas copiadas al portapapeles',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  }

  /**
   * Genera el mensaje con las tasas para copiar
   */
  private setMessage(): string {
    // Tasas Web (Admin) - índices 3, 4, 5
    const webRates = [this.tasas[5], this.tasas[4], this.tasas[3]];
    // Tasas Verde (Client) - índices 0, 1, 2
    const whatsappRates = [this.tasas[2], this.tasas[1], this.tasas[0]];

    const formatRates = (rates: any[]) =>
      rates.map(this.calculateData).join('\n\n');

    return `💻 *TASA WEB* : pagos realizados en nuestra página web https://www.aguacatewallet.com

${formatRates(whatsappRates)}

Envía dinero de forma confiable a Venezuela con profesionales 💰💼🇻🇪 https://www.aguacatewallet.com`;
  }

  /**
   * Calcula y formatea los datos de una tasa para el mensaje
   */

  private calculateData(data: any): string {
    const rango = parseInt(data.rango.split('-')[0], 10);
    const tasa = parseFloat(data.tasa);
    const antes = (tasa * 1000).toFixed(3);
    const bs = (rango * tasa).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `*${data.tasa}*  a partir de *$${rango.toLocaleString('de-DE')}* pesos o *${bs}* bs`;
  }

  /**
   * Carga la configuración actual de bancos activos/inactivos
   */
  cargarConfigBancos(): void {
    this.mainRatesService.obtenerConfigBancos().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.bancosActivos = response.data;
        }
      },
      error: (error: any) => {
      },
    });
  }

  /**
   * Toggle de activación/desactivación de un banco
   * @param banco - Nombre del banco en mayúsculas (MERCANTIL, BANESCO, VENEZUELA, PROVINCIAL)
   */
  toggleBanco(banco: string): void {
    if (this.bloqueadoPorPermiso()) return;
    const bancoUpper = banco.toUpperCase();
    // Capitalizar el nombre del banco para mostrarlo correctamente
    const bancoCapitalizado = banco.charAt(0).toUpperCase() + banco.slice(1).toLowerCase();

    // Validar que el operador activo sea BINANCE
    if (this.valoresFrontend?.data?.operadorActivo !== 'BINANCE') {
      Swal.fire({
        icon: 'info',
        title: 'Operador no compatible',
        html: `Solo puedes activar/desactivar bancos cuando el operador activo es <b>BINANCE</b>.<br><br>
               Operador actual: <b>${this.valoresFrontend?.data?.operadorActivo || 'No definido'}</b>`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      });
      return;
    }

    const nuevoEstado = !this.bancosActivos[bancoUpper];

    // Validar que no se desactiven todos los bancos
    const bancosActivosCount = Object.values(this.bancosActivos).filter(
      (activo) => activo === true
    ).length;

    if (bancosActivosCount === 1 && !nuevoEstado) {
      Swal.fire({
        icon: 'warning',
        title: 'No se puede desactivar',
        text: 'Debe haber al menos 1 banco activo. No se puede desactivar todos los bancos.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      });
      return;
    }

    // Calcular bancos que quedarán activos/inactivos
    const nuevaCantidad = nuevoEstado ? bancosActivosCount + 1 : bancosActivosCount - 1;
    const bancosActivos = Object.keys(this.bancosActivos)
      .filter(b => b === bancoUpper ? nuevoEstado : this.bancosActivos[b])
      .map(b => b.charAt(0) + b.slice(1).toLowerCase())
      .join(', ');

    // Mostrar confirmación
    Swal.fire({
      icon: 'question',
      title: `¿${nuevoEstado ? 'Activar' : 'Desactivar'} ${bancoCapitalizado}?`,
      html: `
        <p class="mb-3">Banco a <b>${nuevoEstado ? 'ACTIVAR' : 'DESACTIVAR'}</b>: <b>${bancoCapitalizado}</b></p>
        <p class="mb-3">Bancos que quedarán <b>activos</b> (${nuevaCantidad}): <b>${bancosActivos}</b></p>
        <p class="text-sm text-gray-600">El promedio de Binance se calculará con estos bancos activos y las tasas se recalcularán automáticamente.</p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
    }).then((result) => {
      if (result.isConfirmed) {
        // Mostrar loading mientras se procesa
        Swal.fire({
          title: `${nuevoEstado ? 'Activando' : 'Desactivando'} ${bancoCapitalizado}...`,
          html: '<p class="mb-3">Actualizando configuración y recalculando tasas</p>',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        this.isUpdating = true;
        const config = { [bancoUpper]: nuevoEstado };

        this.mainRatesService.actualizarConfigBancos(config).subscribe({
          next: (response) => {
            if (response.success) {
              this.bancosActivos[bancoUpper] = nuevoEstado;
              // `tasasGrabadas` con alguna en false: el banco quedó, pero esas
              // tasas no se grabaron. Su `message`, como aviso (no como éxito).
              const sinGrabar = avisoTasasNoGrabadas(response);
              this.avisoTasas = sinGrabar;

              if (sinGrabar) {
                Swal.fire({
                  icon: 'warning',
                  title: `${bancoCapitalizado} ${nuevoEstado ? 'activado' : 'desactivado'}, pero las tasas no se grabaron`,
                  text: sinGrabar,
                  toast: true,
                  position: 'top-end',
                  showConfirmButton: false,
                  timer: 8000,
                  timerProgressBar: true,
                });
              } else {
                Swal.fire({
                  icon: 'success',
                  title: '¡Completado!',
                  html: `
                    <p><b>${bancoCapitalizado}</b> ha sido <b>${nuevoEstado ? 'activado' : 'desactivado'}</b> correctamente</p>
                    ${response.data?.tasasRecalculadas ? '<p class="text-sm text-green-600 mt-2">✓ Tasas recalculadas automáticamente</p>' : ''}
                  `,
                  toast: true,
                  position: 'top-end',
                  showConfirmButton: false,
                  timer: 4000,
                  timerProgressBar: true,
                });
              }

              // Si se recalcularon las tasas, recargar valores
              if (response.data?.tasasRecalculadas) {
                this.cargarValoresIniciales(true);
                this.cargarValoresRegistrados(true);
                this.cargarUsdtVesConfig(); // Actualizar también la tasa USDT-VES
        this.cargarVesUsdtConfig(); // Actualizar también la tasa VES-USDT
                this.refrescarMargenesVes(); // Otro promedio Binance: otro spot cruzado
              }
            }
            this.isUpdating = false;
          },
          error: (error: any) => {
            Swal.fire({
              icon: 'error',
              title: 'Error al actualizar',
              html: `
                <p>No se pudo ${nuevoEstado ? 'activar' : 'desactivar'} <b>${bancoCapitalizado}</b></p>
                <p class="text-sm text-gray-600 mt-2">${error.message || 'Error desconocido'}</p>
              `,
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 5000,
              timerProgressBar: true,
            });
            this.isUpdating = false;
          },
        });
      }
    });
  }

  /**
   * Verifica si un banco está activo
   * @param banco - Nombre del banco en cualquier formato
   */
  esBancoActivo(banco: string): boolean {
    return this.bancosActivos[banco.toUpperCase()] === true;
  }

  // ============================================================
  // MÉTODOS PARA TASAS VES → CLP
  // ============================================================

  /**
   * Carga las tasas VES→CLP agrupadas por rango
   */
  cargarTasasVesClp(isAutoUpdate = false): void {
    this.cargandoVesClp = true;
    this.mainRatesService.obtenerTasasVesCLPAgrupadas().subscribe({
      next: (response) => {
        this.cargandoVesClp = false;
        if (response) {
          const { rangoA, rangoB, rangoC } = response;

          // Franjas dinámicas: si viene `tramos` se pintan todas; si no, los
          // tres rangos de siempre.
          const dinamicas = filasWebDesdeTramos(response, 'Client');
          this.tramosVesClpDinamicos = !!dinamicas;

          const nuevasTasas = dinamicas || [
            {
              rango: rangoA?.client?.rango || '10000-99999',
              descripcion: rangoA?.client?.descripcion || '',
              tasa: rangoA?.client?.tasa,
              perfil: 'Client',
            },
            {
              rango: rangoB?.client?.rango || '100000-209999',
              descripcion: rangoB?.client?.descripcion || '',
              tasa: rangoB?.client?.tasa,
              perfil: 'Client',
            },
            {
              rango: rangoC?.client?.rango || '210000-5000000',
              descripcion: rangoC?.client?.descripcion || '',
              tasa: rangoC?.client?.tasa,
              perfil: 'Client',
            },
          ];

          // Detectar cambios
          if (isAutoUpdate && this.tasasVesClp.length > 0) {
            this.updatedVesClpIndexes.clear();
            nuevasTasas.forEach((nueva, index) => {
              if (this.tasasVesClp[index] && this.tasasVesClp[index].tasa !== nueva.tasa) {
                this.updatedVesClpIndexes.add(index);
              }
            });
            if (this.updatedVesClpIndexes.size > 0) {
              setTimeout(() => { this.updatedVesClpIndexes.clear(); }, 30000);
            }
          }

          this.tasasVesClp = nuevasTasas;
          this.lastVesClpRefresh = new Date();
        }
      },
      error: (error: any) => {
        // No bloquear la pantalla por este error
        this.cargandoVesClp = false;
      },
    });
  }

  /**
   * El editor de franjas guardó: se recargan en el acto las dos tarjetas
   * «Tasa web del día». Si el back todavía no grabó las tasas (`aplicado`
   * false, el cálculo estaba ocupado), se revisan cada 15 s hasta ~2 min
   * (`esperarFranjasAplicadas`).
   */
  /** Los bloques de precio piden sus tramos solos; tras guardar se les pide de nuevo. */
  private recargarBloquesPrecio(): void {
    this.bloquesPrecio?.forEach((b) => b.cargar());
  }

  alGuardarFranjas(e: FranjasGuardadas): void {
    if (e?.par === 'VES-USDT') {
      // Tramos o comisión VES→USDT: se recarga el bloque (config + tramos).
      this.cargarVesUsdtConfig(true);
      this.recargarBloquesPrecio();
      return;
    }
    if (e?.par === 'USDT-VES') {
      // Tramos USDT→VES: se recarga su bloque (config + tramos).
      this.cargarUsdtVesConfig(true);
      this.recargarBloquesPrecio();
      return;
    }
    if (e?.par === 'CLP-USDT' || e?.par === 'USDT-CLP') {
      // Tramos CLP↔USDT: se relee su bloque de la sección «Tasas CLP ↔ USDT»
      // si está desplegada; plegada, se marca vieja y se lee al abrirla.
      // (No son franjas CLP↔VES: nada más que recargar.)
      this.trasGuardarTramosClp?.(e.par);
      return;
    }
    // Foto de antes de guardar: la lectura fresca que pidió el editor justo
    // antes del PUT; si no la hay, lo que muestra la tarjeta (puede ser vieja
    // si la sección estaba plegada).
    const par: 'CLP-VES' | 'VES-CLP' = e?.par === 'VES-CLP' ? 'VES-CLP' : 'CLP-VES';
    const foto =
      typeof e?.foto === 'string'
        ? e.foto
        : huellaFilasWeb(par === 'CLP-VES' ? this.tramosWebClpVes : this.tasasVesClp);
    const esperadas = e?.esperadas ?? null;
    this.recargarTarjetasFranjas();
    this.cargarResumenFranjas();
    // Franjas nuevas o apagadas: otro reparto de márgenes (y el motor, al día).
    this.refrescarMargenesVes();
    // Un guardado nuevo reemplaza la espera anterior (timer y lectura en vuelo).
    this.cancelarEsperaFranjas();
    // Se espera al cron solo si el lock estaba ocupado. Si el motor no grabó
    // (`motivoNoAplicado`), el editor ya mostró por qué y no hay nada que esperar.
    if (e && e.cambios > 0 && !e.aplicado && !e.motivoNoAplicado) {
      this.aplicandoFranjas.add(par);
      this.esperarFranjasAplicadas(par, foto, esperadas, 1, this.esperaGen);
    }
  }

  /** Lectura de las últimas tasas del par (la misma de la tarjeta). */
  private leerTarjeta(par: 'CLP-VES' | 'VES-CLP') {
    return par === 'CLP-VES'
      ? this.mainRatesService.obtenerUltimasTasasAgrupadas()
      : this.mainRatesService.obtenerTasasVesCLPAgrupadas();
  }

  /** Huella fresca de la tarjeta del par; '' si falla. La pide el editor antes del PUT. */
  private async leerFotoTarjeta(par: 'CLP-VES' | 'VES-CLP'): Promise<string> {
    try {
      return huellaFilasWeb(filasTarjetaDesde(par, await firstValueFrom(this.leerTarjeta(par))));
    } catch {
      return '';
    }
  }

  /** Avisa al editor abierto en qué paso está lo guardado (línea de 3 pasos). */
  private marcarEditor(par: 'CLP-VES' | 'VES-CLP', paso: PasoAplicacion): void {
    const editor = this.refEditor?.componentInstance;
    if (!editor) return;
    editor.estadoAplicacion = { par, paso, en: paso === 'aplicado' ? new Date() : null };
  }

  /** Corta la espera al cron en curso: timer, lectura en vuelo y generación. */
  private cancelarEsperaFranjas(): void {
    this.esperaGen++;
    this.aplicandoFranjas.clear();
    if (this.recargaFranjasId) clearTimeout(this.recargaFranjasId);
    this.recargaFranjasId = undefined;
    this.esperaSub?.unsubscribe();
    this.esperaSub = undefined;
  }

  /**
   * Guardado que espera al cron: cada `ESPERA_CRON_CADA_MS` (15 s) se leen las
   * últimas tasas del par y, apenas son las de lo guardado (`franjasAplicadas`:
   * las esperadas de la vista previa al r4, o mismos rangos y cambio frente a
   * la foto fresca de antes del PUT), se recargan las tarjetas y se deja de
   * mirar. Tras `ESPERA_CRON_INTENTOS` (~4 min; el cron corre cada 3) sin
   * verlo, se avisa que se aplicará en la próxima actualización. Sin foto, la
   * primera lectura hace de foto.
   */
  private esperarFranjasAplicadas(
    par: 'CLP-VES' | 'VES-CLP',
    foto: string,
    esperadas: TasaEsperada[] | null,
    intento: number,
    gen: number,
  ): void {
    this.recargaFranjasId = setTimeout(() => {
      this.recargaFranjasId = undefined;
      if (gen !== this.esperaGen || this.destruido) return;
      this.esperaSub = this.leerTarjeta(par).subscribe({
        next: (r: any) => {
          if (gen !== this.esperaGen) return;
          const filas = filasTarjetaDesde(par, r);
          const actual = huellaFilasWeb(filas);
          if (franjasAplicadas(filas, esperadas, foto)) {
            this.aplicandoFranjas.delete(par);
            this.marcarEditor(par, 'aplicado');
            this.recargarTarjetasFranjas();
            return;
          }
          this.seguirEsperandoFranjas(par, foto || actual, esperadas, intento, gen);
        },
        error: () => this.seguirEsperandoFranjas(par, foto, esperadas, intento, gen),
      });
    }, ESPERA_CRON_CADA_MS);
  }

  private seguirEsperandoFranjas(
    par: 'CLP-VES' | 'VES-CLP',
    foto: string,
    esperadas: TasaEsperada[] | null,
    intento: number,
    gen: number,
  ): void {
    if (this.destruido || gen !== this.esperaGen) return;
    if (intento < ESPERA_CRON_INTENTOS) {
      this.esperarFranjasAplicadas(par, foto, esperadas, intento + 1, gen);
      return;
    }
    // Se refrescan igual las tarjetas: muestran lo último grabado.
    this.aplicandoFranjas.delete(par);
    this.marcarEditor(par, 'se-aplicara');
    this.recargarTarjetasFranjas();
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'info',
      title: TEXTO_SE_APLICARA,
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true,
    });
  }

  /**
   * Abre el editor de franjas en un modal grande (pantalla completa en móvil).
   * Con `par` = 'VES-USDT' abre directo en la pestaña de tramos VES→USDT.
   *
   * Lo abre cualquier operador. Editar solo los encargados de tasas: el
   * permiso se consulta en el momento (manda el back, `rates/franjas/permisos`,
   * con respaldo en la lista del front) y sin él abre en «Solo lectura».
   */
  async abrirFranjas(par?: 'VES-USDT' | 'USDT-VES' | 'CLP-USDT' | 'USDT-CLP'): Promise<void> {
    // Abrir y ver puede cualquier operador; editar, solo con permiso (si no,
    // el editor abre en «Solo lectura»). El permiso se consulta en el momento.
    const puede = await this.resolverPermisoFranjas();

    const movil = window.innerWidth < 768;
    const ref = this.dialog.open<FranjasEditorComponent, FranjasEditorData>(
      FranjasEditorComponent,
      {
        data: {
          puedeEditar: puede,
          par,
          leerFoto: (p) => this.leerFotoTarjeta(p === 'VES-CLP' ? 'VES-CLP' : 'CLP-VES'),
          // Recién leído por el panel: el editor no lo vuelve a pedir al abrir.
          estadoFranjas:
            this.estadoFranjas && Date.now() - this.estadoFranjas.en < MainRatesComponent.FRESCO_MS
              ? this.estadoFranjas.estado
              : undefined,
        },
        width: movil ? '100vw' : '1200px',
        maxWidth: movil ? '100vw' : '96vw',
        height: movil ? '100vh' : '92vh',
        maxHeight: movil ? '100vh' : '92vh',
        autoFocus: 'first-tabbable',
        restoreFocus: true,
        disableClose: true,
        panelClass: 'franjas-dialog',
        ariaLabelledBy: 'fe-titulo',
      },
    );
    this.refEditor = ref;
    const sub = ref.componentInstance.guardado.subscribe((e) => this.alGuardarFranjas(e));
    ref.afterClosed().subscribe((resultado) => {
      sub.unsubscribe();
      if (this.refEditor === ref) this.refEditor = undefined;
      // «Cambiar comisión» en la pestaña VES → USDT: su modal de siempre.
      if (resultado === RESULTADO_ABRIR_COMISION) this.abrirModalVesUsdt();
    });
  }

  /** Cantidad de franjas, mínimo y máximo por par para la tarjeta. */
  cargarResumenFranjas(): void {
    this.cargandoResumenFranjas = true;
    this.mainRatesService.obtenerEstadoFranjas().subscribe({
      next: (e) => {
        this.cargandoResumenFranjas = false;
        this.lastResumenFranjasRefresh = new Date();
        this.estadoFranjas = { en: Date.now(), estado: e };
        this.resumenFranjas = resumenFranjas(e);
        this.resumenFranjasOk = true;
      },
      error: () => {
        this.cargandoResumenFranjas = false;
        this.estadoFranjas = null;
        this.resumenFranjas = null;
        this.resumenFranjasOk = false;
      },
    });
  }

  private recargarTarjetasFranjas(): void {
    this.cargarTramosClpVes();
    this.cargarTasasVesClp(true);
  }

  /**
   * Franjas dinámicas CLP→VES para la tarjeta «Tasa web del día». Si la
   * respuesta no trae `tramos` (o falla) queda en `null` y la tarjeta sigue
   * con los tres tramos de `valores-frontend`, como siempre.
   */
  cargarTramosClpVes(): void {
    this.pidiendoTramosClpVes = true;
    this.mainRatesService.obtenerUltimasTasasAgrupadas().subscribe({
      next: (response: any) => {
        this.pidiendoTramosClpVes = false;
        this.lastTramosClpVesRefresh = new Date();
        const body = Array.isArray(response?.tramos) ? response : response?.data;
        this.tramosWebClpVes = filasWebDesdeTramos(body, 'Client');
      },
      error: () => {
        this.pidiendoTramosClpVes = false;
        this.tramosWebClpVes = null;
      },
    });
  }

  // ============================================================
  // MÉTODOS PARA TASA USDT → VES
  // ============================================================

  /**
   * Carga la configuración actual de tasa USDT→VES
   */
  cargarUsdtVesConfig(isAutoUpdate = false): void {
    if (!isAutoUpdate) {
      this.loadingUsdtVes = true;
    }
    this.errorUsdtVes = null;
    this.pidiendoUsdtVes = true;

    this.mainRatesService.obtenerUsdtVesConfig().subscribe({
      next: (response) => {
        this.pidiendoUsdtVes = false;

        // Detectar si hubo cambios para mostrar indicador
        if (isAutoUpdate && this.usdtVesConfig?.data?.datosEnVivo && response.data?.datosEnVivo) {
          const prev = this.usdtVesConfig.data.datosEnVivo.tasaFinalCalculada;
          const curr = response.data.datosEnVivo.tasaFinalCalculada;
          if (prev !== curr) {
            this.usdtVesUpdated = true;
            setTimeout(() => { this.usdtVesUpdated = false; }, 30000);
          }
        }

        this.usdtVesConfig = response;
        this.registrarLectura(
          'USDT_VES',
          response.data?.datosEnVivo?.tasaFinalCalculada,
        );
        this.lastUsdtVesRefresh = new Date();
        this.loadingUsdtVes = false;
      },
      error: (error: any) => {
        this.pidiendoUsdtVes = false;
        this.errorUsdtVes = error.message || 'Error al cargar configuración';
        this.loadingUsdtVes = false;
      },
    });
  }

  /**
   * Obtiene los bancos para mostrar en la card USDT-VES
   */
  getBancosUsdtVes(): { nombre: string; valor: number; activo: boolean }[] {
    if (!this.usdtVesConfig?.data?.datosEnVivo?.bancos) return [];

    return Object.entries(this.usdtVesConfig.data.datosEnVivo.bancos).map(
      ([nombre, info]) => ({
        nombre,
        valor: info.valor,
        activo: info.activo,
      })
    );
  }

  /**
   * Guarda la tasa en vivo como nueva tasa vigente (misma comisión, datos Binance actualizados)
   */
  async actualizarTasaUsdtVesAhora(): Promise<void> {
    if (await this.bloqueadoPorPermisoTasas()) return;
    if (!this.usdtVesConfig?.data?.datosEnVivo) return;

    const comision = this.usdtVesConfig.data.datosEnVivo.comisionConfigurada;
    const tasaEnVivo = this.usdtVesConfig.data.datosEnVivo.tasaFinalCalculada;

    this.isUpdating = true;
    this.mainRatesService
      .guardarUsdtVesConfig({ modo: 'AUTOMATICO_PCT', comisionPct: comision })
      .subscribe({
      next: (response) => {
        this.isUpdating = false;
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Tasa actualizada',
            html: `<p>Nueva tasa vigente: <b>1 USDT = ${response.data.tasaFinal} VES</b></p>`,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true,
          });
          this.cargarUsdtVesConfig();
          this.cargarVesUsdtConfig();
        }
      },
      error: (error: any) => {
        this.isUpdating = false;
        if (this.avisarSinPermiso(error)) return;
        Swal.fire({
          icon: 'error',
          title: 'Error al actualizar',
          text: error.message || 'No se pudo guardar la tasa',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 4000,
        });
      },
    });
  }

  /**
   * Abre el modal para configurar la comisión USDT→VES
   */
  async abrirModalUsdtVes(): Promise<void> {
    if (await this.bloqueadoPorPermisoTasas()) return;
    if (!this.usdtVesConfig?.data) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No hay datos disponibles para configurar',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const dialogRef = this.dialog.open(EditUsdtVesModalComponent, {
      ...this.tamanoModal('500px'),
      data: {
        tasaVigente: this.usdtVesConfig.data.tasaVigente,
        datosEnVivo: this.usdtVesConfig.data.datosEnVivo,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {

        Swal.fire({
          icon: 'success',
          title: 'Configuración guardada',
          html: `
            <p>Nueva tasa: <b>1 USDT = ${result.tasaFinal} VES</b></p>
            <p>Modo activo: <b>${result.modo === 'MANUAL_FIJO' ? 'Precio manual fijo' : 'Automático por comisión ' + result.comisionPct + '%'}</b></p>
          `,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 4000,
          timerProgressBar: true,
        });

        // Recargar la configuración
        this.cargarUsdtVesConfig();
        this.cargarVesUsdtConfig();
      }
    });
  }

  // ============================================================
  // MÉTODOS PARA TASA VES → USDT
  // ============================================================

  /**
   * Carga la configuración actual de tasa VES→USDT. `conTramos` (por defecto
   * sí): también los tramos y sus últimos cambios; el latido lo apaga mientras
   * la sección está plegada (la alarma de desvío solo usa la config).
   */
  cargarVesUsdtConfig(isAutoUpdate = false, conTramos = true): void {
    if (!isAutoUpdate) {
      this.loadingVesUsdt = true;
    }
    this.errorVesUsdt = null;
    this.pidiendoVesUsdt = true;

    this.mainRatesService.obtenerVesUsdtConfig().subscribe({
      next: (response) => {
        this.pidiendoVesUsdt = false;
        // Detectar si hubo cambios para mostrar indicador
        if (isAutoUpdate && this.vesUsdtConfig?.data?.datosEnVivo && response.data?.datosEnVivo) {
          const prev = this.vesUsdtConfig.data.datosEnVivo.tasaFinalCalculada;
          const curr = response.data.datosEnVivo.tasaFinalCalculada;
          if (prev !== curr) {
            this.vesUsdtUpdated = true;
            setTimeout(() => { this.vesUsdtUpdated = false; }, 30000);
          }
        }

        this.vesUsdtConfig = response;
        this.registrarLectura(
          'VES_USDT',
          response.data?.datosEnVivo?.tasaFinalCalculada,
        );
        this.lastVesUsdtRefresh = new Date();
        this.loadingVesUsdt = false;
      },
      error: (error: any) => {
        this.pidiendoVesUsdt = false;
        this.errorVesUsdt = error.message || 'Error al cargar configuración VES-USDT';
        this.loadingVesUsdt = false;
      },
    });
    // Los tramos viajan con el mismo latido que la tasa VES→USDT.
    if (conTramos) this.cargarTramosVesUsdt(isAutoUpdate);
  }

  /**
   * Tramos VES→USDT (GET de operadores, con costo y márgenes) y los últimos
   * cambios. En una recarga automática que falla se conserva lo que había (no
   * se salta a la vista vieja por un parpadeo).
   *
   * Los últimos cambios salen del `historial` que ya trae el editor (página 1);
   * el historial aparte solo se pide si esa página no alcanza para los 4.
   */
  cargarTramosVesUsdt(isAutoUpdate = false): void {
    this.pidiendoTramosVesUsdt = true;
    this.mainRatesService.obtenerEditorTramosVesUsdt().subscribe({
      next: (r) => {
        this.pidiendoTramosVesUsdt = false;
        this.lastTramosVesUsdtRefresh = new Date();
        this.tramosVesUsdt = r;
        this.ponerUltimosCambiosVesUsdt(r?.historial, isAutoUpdate);
      },
      error: () => {
        this.pidiendoTramosVesUsdt = false;
        if (!isAutoUpdate) {
          this.tramosVesUsdt = null;
          this.ultimosCambiosVesUsdt = null;
        }
      },
    });
  }

  /**
   * Se muestran 4: las ediciones que solo mueven el orden se esconden. Si la
   * página del editor ya tiene 4 visibles, o es todo el historial, son los
   * mismos 4 que daba el GET aparte de 10 (la página del editor es su
   * comienzo); si no, se pide ese GET como antes.
   */
  private ponerUltimosCambiosVesUsdt(h: HistorialTramosUsdtPagina | null | undefined, isAutoUpdate: boolean): void {
    const items = Array.isArray(h?.items) ? h!.items : null;
    if (items) {
      const visibles = TU.historialVisible(items);
      const total = Number(h?.total);
      const completo = Number.isFinite(total) && total <= items.length;
      if (visibles.length >= 4 || completo) {
        this.ultimosCambiosVesUsdt = visibles.slice(0, 4);
        return;
      }
    }
    this.mainRatesService.obtenerHistorialTramosVesUsdt(1, 10).subscribe({
      next: (hist) => (this.ultimosCambiosVesUsdt = TU.historialVisible(hist?.items).slice(0, 4)),
      error: () => {
        if (!isAutoUpdate) this.ultimosCambiosVesUsdt = null;
      },
    });
  }

  /** Hay tramos para pintar; si no, el bloque queda como antes. */
  get hayTramosVesUsdt(): boolean {
    return !!this.tramosVesUsdt?.tasas?.length;
  }

  get listaTramosVesUsdt(): TasaTramoUsdt[] {
    return this.tramosVesUsdt?.tasas || [];
  }

  /** El piso que manda el back; si no viene, la misma cuenta (con la venta USDT→VES). */
  get pisoVesUsdt(): number | null {
    const p = Number(this.tramosVesUsdt?.piso);
    if (this.tramosVesUsdt?.piso != null && p > 0) return p;
    return TU.pisoDe(
      this.tramosVesUsdt?.referencia,
      this.tramosVesUsdt?.margenMinPct,
      this.tramosVesUsdt?.tasaVentaUsdtVes,
    );
  }

  /** Aviso de tasa fija manual: solo en MANUAL_FIJO, con la base y el piso. */
  get avisoManualVesUsdt(): string {
    const modo = this.tramosVesUsdt?.modoBase ?? this.vesUsdtConfig?.data?.tasaVigente?.modo;
    if (modo !== 'MANUAL_FIJO') return '';
    const base = this.tramosVesUsdt?.base ?? this.vesUsdtConfig?.data?.tasaVigente?.tasaFinal;
    return TU.avisoManualFijo(base, this.pisoVesUsdt);
  }

  get espacioVesUsdt(): number | null {
    return TU.espacioDe(
      this.tramosVesUsdt?.referencia,
      this.tramosVesUsdt?.base,
      this.tramosVesUsdt?.margenMinPct,
    );
  }

  letraTramo(i: number): string {
    return TU.letraDe(i);
  }

  /** Ancho de la barra de margen: relativo al margen del primer tramo. */
  anchoMargenTramo(t: TasaTramoUsdt): number {
    const max = Number(this.listaTramosVesUsdt[0]?.margenPct);
    const m = Number(t?.margenPct);
    if (!(max > 0) || !Number.isFinite(m)) return 0;
    return Math.max(0, Math.min(100, (m / max) * 100));
  }

  /** Dónde cae la línea del candado en la barra, en %. */
  get candadoVesUsdtPct(): number | null {
    const max = Number(this.listaTramosVesUsdt[0]?.margenPct);
    const min = Number(this.tramosVesUsdt?.margenMinPct);
    if (!(max > 0) || !Number.isFinite(min)) return null;
    return Math.max(0, Math.min(100, (min / max) * 100));
  }

  rangoTramoVesUsdt(i: number): string {
    const t = this.listaTramosVesUsdt[i];
    if (!t) return '—';
    return TU.etiquetaRangoUsdt(t.desde, t.hasta, i === this.listaTramosVesUsdt.length - 1);
  }

  /** Simulador del bloque: tramo, tasa, lo que recibe y la mejora vs la base. */
  get simVesUsdt(): {
    indice: number;
    tasa: number;
    recibe: number;
    recibeBase: number;
    mejoraUsdt: number;
    mejoraPct: number;
    margenPct: number;
    margenVes: number | null;
  } | null {
    const lista = this.listaTramosVesUsdt;
    const base = this.tramosVesUsdt?.base;
    const sim = TU.simular(lista, base, this.simVesUsdtMonto);
    if (sim.indice < 0) return null;
    const t = lista[sim.indice];
    const recibe = TU.recibeUsdt(this.simVesUsdtMonto, t.tasa);
    const recibeBase = TU.recibeUsdt(this.simVesUsdtMonto, base);
    if (recibe == null || recibeBase == null) return null;
    const ref = Number(this.tramosVesUsdt?.referencia);
    return {
      indice: sim.indice,
      tasa: t.tasa,
      recibe,
      recibeBase,
      mejoraUsdt: recibe - recibeBase,
      mejoraPct: recibeBase > 0 ? (recibe / recibeBase - 1) * 100 : 0,
      margenPct: t.margenPct,
      // Lo que queda para Aguacate: lo que paga menos lo que cuestan esos USDT.
      margenVes: ref > 0 ? Number(this.simVesUsdtMonto) - recibe * ref : null,
    };
  }

  etiquetaClaveTramo(c: string): string {
    return TU.etiquetaClave(c);
  }

  resumenCambioTramo(h: HistorialTramoUsdt): string {
    return TU.resumenHistorial(h);
  }

  /**
   * Guarda la tasa en vivo VES→USDT como nueva tasa vigente
   */
  async actualizarTasaVesUsdtAhora(): Promise<void> {
    if (await this.bloqueadoPorPermisoTasas()) return;
    if (!this.vesUsdtConfig?.data?.datosEnVivo) return;

    const comision = this.vesUsdtConfig.data.datosEnVivo.comisionConfigurada;

    this.isUpdating = true;
    this.mainRatesService
      .guardarVesUsdtConfig({ modo: 'AUTOMATICO_PCT', comisionPct: comision })
      .subscribe({
      next: (response) => {
        this.isUpdating = false;
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Tasa VES→USDT actualizada',
            html: `<p>Nueva tasa vigente: <b>1 USDT = ${response.data.tasaFinal} VES</b></p>`,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true,
          });
          this.cargarVesUsdtConfig();
        }
      },
      error: (error: any) => {
        this.isUpdating = false;
        if (this.avisarSinPermiso(error)) return;
        Swal.fire({
          icon: 'error',
          title: 'Error al actualizar',
          text: error.message || 'No se pudo guardar la tasa VES-USDT',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 4000,
        });
      },
    });
  }

  /**
   * Abre el modal para configurar la comisión VES→USDT
   */
  async abrirModalVesUsdt(): Promise<void> {
    if (await this.bloqueadoPorPermisoTasas()) return;
    if (!this.vesUsdtConfig?.data) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No hay datos disponibles para configurar VES-USDT',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const dialogRef = this.dialog.open(EditVesUsdtModalComponent, {
      ...this.tamanoModal('500px'),
      data: {
        tasaVigente: this.vesUsdtConfig.data.tasaVigente,
        datosEnVivo: this.vesUsdtConfig.data.datosEnVivo,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        Swal.fire({
          icon: 'success',
          title: 'Configuración VES→USDT guardada',
          html: `
            <p>Nueva tasa: <b>1 USDT = ${result.tasaFinal} VES</b></p>
            <p>Modo activo: <b>${result.modo === 'MANUAL_FIJO' ? 'Precio manual fijo' : 'Automático por comisión ' + result.comisionPct + '%'}</b></p>
          `,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 4000,
          timerProgressBar: true,
        });

        this.cargarVesUsdtConfig();
      }
    });
  }

  // ============================================================
  // TASAS CLP ↔ USDT (sección propia, 22/10/2026)
  // ============================================================

  /**
   * Lee el `/editor` de un par CLP (compra o venta). 404/503 = backend viejo
   * o sin SQL: el bloque muestra «Tramos no disponibles» y el panel sigue. En
   * una recarga automática que falla se conserva lo que había.
   */
  recargaDe(par: ParClp): number {
    return this.recargaClp[par];
  }

  esDistanciaClp(par: ParClp): boolean {
    return !!this.distanciaClp[par];
  }

  /** Lo emite `app-distancia-bloque` (fuera del ciclo de detección). */
  ponerDistanciaClp(par: ParClp, distancia: boolean): void {
    this.distanciaClp = { ...this.distanciaClp, [par]: !!distancia };
  }

  cargarTramosClp(par: ParClp, isAutoUpdate = false): void {
    const carga = this.cargaClp[par];
    if (carga.cargando) return;
    carga.cargando = true;
    const pedido: Observable<EditorTramosClpUsdtResponse | EditorTramosUsdtClpResponse> =
      par === 'CLP-USDT'
        ? this.mainRatesService.obtenerEditorTramosClpUsdt()
        : this.mainRatesService.obtenerEditorTramosUsdtClp();
    pedido.subscribe({
      next: (r: any) => {
        carga.cargando = false;
        carga.leido = new Date();
        carga.noDisponible = false;
        carga.error = null;
        this.vistaClp[par] = par === 'CLP-USDT' ? vistaCompra(r) : vistaVenta(r);
        this.recargaClp = { ...this.recargaClp, [par]: this.recargaClp[par] + 1 };
      },
      error: (err: any) => {
        carga.cargando = false;
        const status = statusDe(err);
        if (status === 404 || status === 503) {
          carga.noDisponible = true;
          carga.error = null;
          this.vistaClp[par] = null;
          return;
        }
        if (isAutoUpdate && this.vistaClp[par]) return;
        carga.error = err?.message || 'No se pudieron leer los tramos.';
      },
    });
  }

  /** Tras guardar en el modal: desplegada se relee; plegada queda vieja. */
  private trasGuardarTramosClp(par: ParClp): void {
    if (this.abierta('clpUsdt')) {
      this.cargarTramosClp(par, true);
    } else {
      this.cargaClp[par].leido = null;
    }
  }

  /** Resumen de una línea de la sección plegada (vacío si no se leyó). */
  get resumenSeccionClp(): string {
    return resumenClp(this.vistaClp['CLP-USDT'], this.vistaClp['USDT-CLP']);
  }

  /** Algún bloque tiene aviso (tramos en la base o que no rigen). */
  get hayAvisoClp(): boolean {
    return !!(this.vistaClp['CLP-USDT']?.aviso || this.vistaClp['USDT-CLP']?.aviso);
  }

  /** La lectura más vieja de los dos bloques (para «Actualizado hace…»). */
  get leidoClp(): Date | null {
    const fechas = this.paresClp.map((p) => this.cargaClp[p].leido).filter((d): d is Date => !!d);
    if (!fechas.length) return null;
    return fechas.reduce((a, b) => (a.getTime() <= b.getTime() ? a : b));
  }

  vistaDe(par: ParClp): VistaBloqueClp | null {
    return this.vistaClp[par];
  }

  cargaDe(par: ParClp): CargaBloqueClp {
    return this.cargaClp[par];
  }

  montoSimDe(par: ParClp): number | null {
    return this.simClpMonto[par];
  }

  ponerMontoSim(par: ParClp, monto: number | null): void {
    this.simClpMonto = { ...this.simClpMonto, [par]: monto };
  }

  simClp(par: ParClp): SimulacionClp | null {
    return simularClp(this.vistaClp[par], this.simClpMonto[par]);
  }

  rangoClp(par: ParClp, i: number): string {
    return rangoTramoClp(this.vistaClp[par], i);
  }

  // ============================================================
  // PAGOS VENEZUELA — BP prioritario + N58 fallback (toggles independientes)
  //
  // Los dos toggles pueden coexistir en ON. El router del backend
  // (`assignRetiroVesAutomatically`) intenta BP primero; si los gates BCV no
  // aplican (cuota agotada, monto > 100 USD eq, tasa stale), cae a N58.
  // Si ambos OFF, las operaciones quedan en manual.
  // ============================================================

  /**
   * Hidrata el estado de los toggles VE.
   *
   * Estrategia resiliente:
   *  - PRIMERO carga N58 por su endpoint legacy (sabemos que existe).
   *  - DESPUÉS intenta `getToggles()` para hidratar BP. Si falla (deploy
   *    pendiente, gateway, 401, etc.) NO rompe la UI ni la sesión:
   *    deja BP en su estado default (OFF) y muestra error solo en su sub-card.
   *  - El endpoint `getToggles()` está marcado con `X-Skip-Error-Interceptor`
   *    para que ningún 401 espurio dispare logout global.
   */
  cargarTogglesVE(): void {
    this.cargarEstadoN58();
    this.cargarTogglesBP();
    this.cargarEstadoR4();
  }

  private cargarEstadoR4(): void {
    this.loadingR4Pagos = true;
    this.r4PagosError = null;

    this.r4Service.getPagoR4Status().subscribe({
      next: (res) => {
        this.r4PagosActivo = res.active;
        this.loadingR4Pagos = false;
      },
      error: (err) => {
        this.r4PagosError = typeof err === 'string' ? err : err?.message || 'R4 no disponible (deploy pendiente)';
        this.loadingR4Pagos = false;
      },
    });
  }

  toggleR4Pagos(): void {
    const nuevoEstado = !this.r4PagosActivo;

    Swal.fire({
      icon: 'question',
      title: `¿${nuevoEstado ? 'Activar' : 'Desactivar'} pagos R4?`,
      html: `<p>Los pagos a Venezuela vía R4 Conecta serán <b>${nuevoEstado ? 'ACTIVADOS' : 'DESACTIVADOS'}</b>.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: nuevoEstado ? '#16a34a' : '#dc2626',
      cancelButtonColor: '#6b7280',
    }).then((result) => {
      if (result.isConfirmed) {
        this.loadingR4Pagos = true;
        this.r4PagosError = null;

        this.r4Service.togglePagoR4().subscribe({
          next: (res) => {
            this.r4PagosActivo = res.active;
            this.loadingR4Pagos = false;
            Swal.fire({
              icon: 'success',
              title: `Pagos R4 ${res.active ? 'activados' : 'desactivados'}`,
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            });
          },
          error: (err: string) => {
            this.loadingR4Pagos = false;
            const msg = typeof err === 'string' ? err : 'Error al cambiar estado de pagos R4';
            this.r4PagosError = msg;
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: msg,
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 4000,
              timerProgressBar: true,
            });
          },
        });
      }
    });
  }

  private cargarEstadoN58(): void {
    this.loadingN58Pagos = true;
    this.n58PagosError = null;

    this.n58Service.getPagoN58Status().subscribe({
      next: (res) => {
        this.n58PagosActivo = res.active;
        this.loadingN58Pagos = false;
      },
      error: (err) => {
        this.n58PagosError = typeof err === 'string' ? err : err?.message || 'Error al consultar estado N58';
        this.loadingN58Pagos = false;
      },
    });
  }

  private cargarTogglesBP(): void {
    this.loadingBPPagos = true;
    this.bpPagosError = null;

    this.bancoPlazaService.getToggles().subscribe({
      next: (status: BancoPlazaTogglesStatus) => {
        this.bpPagosActivo = status.pagoBancoPlaza;
        // El backend nuevo también devuelve pagoN58 — si difiere del legacy, gana este (es la fuente más fresca)
        if (typeof status.pagoN58 === 'boolean') {
          this.n58PagosActivo = status.pagoN58;
        }
        this.loadingBPPagos = false;
      },
      error: (err) => {
        // No tumbar la sesión ni el dashboard. Solo logueamos y mostramos un aviso en la sub-card BP.
        const msg = typeof err === 'string' ? err : err?.message || 'Banco Plaza no disponible (deploy pendiente)';
        this.bpPagosError = msg;
        this.loadingBPPagos = false;
      },
    });
  }

  toggleN58Pagos(): void {
    const nuevoEstado = !this.n58PagosActivo;

    Swal.fire({
      icon: 'question',
      title: `¿${nuevoEstado ? 'Activar' : 'Desactivar'} pagos N58?`,
      html: `<p>Los pagos a Venezuela vía N58 serán <b>${nuevoEstado ? 'ACTIVADOS' : 'DESACTIVADOS'}</b>.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: nuevoEstado ? '#16a34a' : '#dc2626',
      cancelButtonColor: '#6b7280',
    }).then((result) => {
      if (result.isConfirmed) {
        this.loadingN58Pagos = true;
        this.n58PagosError = null;

        this.n58Service.togglePagoN58().subscribe({
          next: (res) => {
            this.n58PagosActivo = res.active;
            this.loadingN58Pagos = false;

            Swal.fire({
              icon: 'success',
              title: `Pagos N58 ${res.active ? 'activados' : 'desactivados'}`,
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            });
          },
          error: (err: N58ToggleError | string) => {
            this.loadingN58Pagos = false;

            // 409 dead-code defensivo: el backend ya no rechaza por
            // exclusividad N58⊕BP (derogada), pero si en el futuro
            // se reintroduce algún conflict-state, este handler resincroniza.
            if (typeof err === 'object' && err.status === 409) {
              this.cargarTogglesVE();
              Swal.fire({
                icon: 'warning',
                title: 'No se pudo cambiar el estado',
                html: `<p>${err.message}</p>`,
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#f59e0b',
              });
              return;
            }

            const msg =
              typeof err === 'string'
                ? err
                : err?.message || 'Error al cambiar estado de pagos N58';
            this.n58PagosError = msg;
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: msg,
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 4000,
              timerProgressBar: true,
            });
          },
        });
      }
    });
  }

  toggleBPPagos(): void {
    const nuevoEstado = !this.bpPagosActivo;

    Swal.fire({
      icon: 'question',
      title: `¿${nuevoEstado ? 'Activar' : 'Desactivar'} pagos Banco Plaza?`,
      html: `<p>Los pagos a Venezuela vía <b>Banco Plaza</b> serán <b>${nuevoEstado ? 'ACTIVADOS' : 'DESACTIVADOS'}</b>.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: nuevoEstado ? '#16a34a' : '#dc2626',
      cancelButtonColor: '#6b7280',
    }).then((result) => {
      if (result.isConfirmed) {
        this.loadingBPPagos = true;
        this.bpPagosError = null;

        this.bancoPlazaService.setPagoBancoPlaza(nuevoEstado).subscribe({
          next: (status: BancoPlazaTogglesStatus) => {
            this.bpPagosActivo = status.pagoBancoPlaza;
            this.n58PagosActivo = status.pagoN58;
            this.loadingBPPagos = false;

            Swal.fire({
              icon: 'success',
              title: `Banco Plaza ${status.pagoBancoPlaza ? 'activado' : 'desactivado'}`,
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            });
          },
          error: (err: BancoPlazaToggleError) => {
            this.loadingBPPagos = false;

            // 409 dead-code defensivo: el backend ya no rechaza por
            // exclusividad N58⊕BP (derogada), pero si en el futuro
            // se reintroduce algún conflict-state, este handler resincroniza.
            if (err?.status === 409) {
              this.cargarTogglesVE();
              Swal.fire({
                icon: 'warning',
                title: 'No se pudo cambiar el estado',
                html: `<p>${err.message}</p>`,
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#f59e0b',
              });
              return;
            }

            const msg = err?.message || 'Error al cambiar estado de Banco Plaza';
            this.bpPagosError = msg;
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: msg,
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 4000,
              timerProgressBar: true,
            });
          },
        });
      }
    });
  }

}
